<template>
<div>
    <div class="calcDescription peManagement" id="peManagement-header">
        <h2>{{getLocale(content.labels.tool_title)}}</h2>
    </div>

    <div class="peManagement calcItem groupItem" :class="{standalone: options.standalone}">
        <div id="peManagement">

            <!-- v-form mirrors peClassification. Enter is intercepted because this sits inside
                 form#calcWrapper, where Enter in a text field would "click" the footer Reset button. -->
            <v-form ref="peManagement" v-model="validForm" @submit.prevent="handleSubmit" @keydown.enter.native="onEnterKey">

                <div v-if="options.showLanguageToggle" class="bm-lang-toggle" role="group" :aria-label="getLocale(content.labels.language_toggle_label)">
                    <button
                        type="button"
                        class="bm-lang-pill"
                        :class="{active: activeLanguage === 'en-ca'}"
                        @click="setLanguage('en-ca')">English</button>
                    <button
                        type="button"
                        class="bm-lang-pill"
                        :class="{active: activeLanguage === 'fr-ca'}"
                        @click="setLanguage('fr-ca')">Français</button>
                </div>

                <PeManagementTool
                    v-if="ready"
                    ref="tool"
                    :language="toolLanguage"
                    :pdf-handler="onSummaryPdfLink"
                    @change="onToolChange"/>

                <div v-if="toolState.hasAnswers" class="bm-actions">
                    <button type="button" class="bm-btn bm-btn-secondary" @click="startOver()">
                        {{getLocale(content.labels.start_over_button)}}
                    </button>
                    <button v-if="toolState.summaryVisible" type="button" class="bm-btn bm-btn-primary" @click="download()">
                        {{getLocale(content.labels.download_pdf)}}
                    </button>
                </div>
            </v-form>

            <!-- Printable / PDF summary, follows the peClassification PDF pattern.
                 The content is the tool's own patient summary (current language, hidden rows removed). -->
            <vue-html2pdf
                ref="html2Pdf_recommendations"
                :show-layout="false"
                :float-layout="true"
                :enable-download="false"
                :preview-modal="true"
                :manual-pagination="true"
                :html-to-pdf-options="pdfOptions"
                filename="pe-management-summary"
                pdf-format="letter"
                pdf-orientation="portrait"
                pdf-content-width="100%"
                @hasDownloaded="ui.snackbar.display = false">
                <section slot="pdf-content">
                    <div v-if="pdfDownload.html" class="vm-pe bm-pdf-summary" :class="'vm-lang-' + toolLanguage">
                        <div class="peSummary" v-html="pdfDownload.html"></div>
                    </div>
                </section>
            </vue-html2pdf>

            <div v-if="ui.snackbar.display" class="bm-snackbar">{{ui.snackbar.message}}</div>

        </div>
    </div>
</div>
</template>

<script>
import VueHtml2pdf from 'vue-html2pdf'
import PeManagementTool from './PeManagementTool.vue'

/* Wrapper labels. The clinical content itself (questions, recommendations, summary, both
 * languages) lives in PeManagementTool.vue, ported from vivomap survey 261. */
const CONTENT = {
    labels: {
        tool_title: {"en-ca": "Pulmonary Embolism", "fr-ca": "Embolie pulmonaire"},
        language_toggle_label: {"en-ca": "Language", "fr-ca": "Langue"},
        start_over_button: {"en-ca": "Start Over", "fr-ca": "Recommencer"},
        download_pdf: {"en-ca": "Download Summary", "fr-ca": "Télécharger le PDF"},
        generate_pdf: {"en-ca": "Generating PDF...", "fr-ca": "Génération du PDF..."}
    }
}

export default {
    name: "peManagement",
    props: {
        language: {
            type: String,
            required: false,
            default: "en-ca"
        },
        options: {
            type: Object,
            required: false,
            default: () => ({
                showLanguageToggle: true,
                autoLaunch: false,
                standalone: true
            })
        }
    },
    components: {
        VueHtml2pdf,
        PeManagementTool
    },
    created(){
        this.init()
    },
    beforeDestroy(){
        clearTimeout(this.timer)
    },
    data(){
        return {
            content: CONTENT,
            ui: {
                snackbar: {
                    display: false,
                    message: 'Generating PDF',
                    timeout: -1,
                }
            },
            validForm: false,
            ready: false,
            activeLanguage: this.language,
            toolState: {
                hasAnswers: false,
                summaryVisible: false
            },
            timer: null,
            pdfDownload: {
                generatingPDF: false,
                html: ''
            },
            pdfOptions: {
                margin: [0.4, 0.4, 0.5, 0.4],
                filename: 'pe-management-summary.pdf',
                image: {type: 'jpeg', quality: 0.98},
                enableLinks: true,
                html2canvas: {scale: 2, useCORS: true},
                jsPDF: {unit: 'in', format: 'letter', orientation: 'portrait'},
                // the summary is one long table: keep rows, list items and paragraphs whole
                pagebreak: {mode: ['css', 'legacy'], avoid: ['tr', 'li', 'p', '.dont-break']}
            }
        }
    },
    methods: {
        init(){
            this.toolState = {hasAnswers: false, summaryVisible: false}
            this.pdfDownload.html = ''
            this.ui.snackbar.display = false
            if (this.$refs.tool) this.$refs.tool.reset()

            this.ready = true

            let self = this
            setTimeout(() => {
                self.scrollTo('peManagement-header')
            }, 1200)
        },
        handleSubmit(){
            return
        },
        onEnterKey(e){
            // Enter in a number/INR field commits the value (same as leaving the field) instead
            // of submitting the surrounding page form.
            if (e.target && e.target.tagName === 'INPUT') {
                e.preventDefault()
                e.target.blur()
            }
        },
        setLanguage(lang){
            this.activeLanguage = lang
            this.$emit('update:language', lang)
        },
        onToolChange(state){
            this.toolState = {
                hasAnswers: !!state.hasAnswers,
                summaryVisible: !!state.summaryVisible
            }
        },
        startOver(){
            this.init()
        },
        // The summary's own "Click here to generate a PDF of this summary." link lands here.
        // Returning null keeps that link in place (the PDF opens in the preview instead).
        onSummaryPdfLink(){
            this.download()
            return null
        },
        download(){
            if (!this.$refs.tool) return
            this.pdfDownload.html = this.cleanSummaryHtml(this.$refs.tool.summaryHtml())
            if (!this.pdfDownload.html) return

            let self = this
            this.ui.snackbar.message = this.getLocale(this.content.labels.generate_pdf)
            this.ui.snackbar.display = true
            clearTimeout(self.timer)
            self.timer = setTimeout(() => {
                self.$refs.html2Pdf_recommendations.generatePdf()
            }, 800);
        },
        cleanSummaryHtml(html){
            if (!html) return ''
            const holder = document.createElement('div')
            holder.innerHTML = html
            // on-screen controls that don't belong on paper
            holder.querySelectorAll('.click_button, #pdf_link, #pdf_link_fr, [data-toggle], [data-table], .btn').forEach(el => el.remove())
            holder.querySelectorAll('center').forEach(el => { if (!el.textContent.trim()) el.remove() })
            return holder.innerHTML
        },
        scrollTo(targetElemId){
            let el = document.querySelector('#' + targetElemId)
            return el && el.scrollIntoView({behavior: 'smooth'});
        },
        getLocale(source){
            if (source){
                return source[this.activeLanguage] === undefined || !source[this.activeLanguage] ? source["en-ca"] : source[this.activeLanguage]
            }
            return null
        },
    },
    computed: {
        // the tool takes 'en' / 'fr'; the site uses 'en-ca' / 'fr-ca'
        toolLanguage(){
            return String(this.activeLanguage || '').toLowerCase().indexOf('fr') === 0 ? 'fr' : 'en'
        },
        isMobile(){
            return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) && !window.MSStream
        }
    },
    watch: {
        language(after){
            this.activeLanguage = after
        },
        activeLanguage(){
            this.pdfDownload.html = ''
            let self = this
            setTimeout(() => {
                self.scrollTo('peManagement-header')
            }, 200)
        }
    }
}
</script>

<style lang="scss" scoped>
.bm-lang-toggle {
    display: flex;
    gap: 6px;
    margin: 10px 15px 12px;
}
.bm-lang-pill {
    border: 1px solid #B0B6C8;
    background: #fff;
    border-radius: 20px;
    padding: 5px 14px;
    font-size: 0.85em;
    cursor: pointer;
}
.bm-lang-pill.active {
    background: #1B2E6B;
    border-color: #1B2E6B;
    color: #fff;
}
.bm-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin: 0 15px 20px;
}
.bm-btn {
    font-family: inherit;
    font-size: 0.95em;
    border-radius: 6px;
    padding: 10px 18px;
    cursor: pointer;
    border: 1px solid #B0B6C8;
    background: #fff;
    color: #1A1A2E;
}
.bm-btn-primary {
    background: #1B2E6B;
    border-color: #1B2E6B;
    color: #fff;
}
.bm-btn-secondary {
    background: #fff;
    border-color: #1B2E6B;
    color: #1B2E6B;
}
.bm-btn:focus-visible, .bm-lang-pill:focus-visible {
    outline: 2px solid #1B2E6B;
    outline-offset: 2px;
}
.bm-snackbar {
    position: fixed;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    background: #1A1A2E;
    color: #fff;
    padding: 10px 20px;
    border-radius: 6px;
    z-index: 1000;
}
</style>

<style>
/* Clinical tools page overrides that need ID weight:
   ClinicalTools.vue styles `#calcWrapper a` white + italic for the dark menu. */
#peManagement .vm-pe a {
    color: #428bca;
    font-style: normal;
}
#peManagement .vm-pe a:hover,
#peManagement .vm-pe a:focus {
    color: #2a6496;
}
#peManagement .vm-pe a.btn-info,
#peManagement .vm-pe a.btn-primary,
#peManagement .vm-pe a.btn-info:hover,
#peManagement .vm-pe a.btn-primary:hover {
    color: #fff;
}
/* Blend into the clinical tools page: use the site's font, and drop the vivomap frames
   (shadowed boxes around popups/recommendations, dotted dividers under answer rows). */
#peManagement .vm-pe {
    font-family: inherit;
}
#peManagement .vm-pe .questionBox.popup,
#peManagement .vm-pe .widget {
    box-shadow: none;
    border: none;
    width: 100%;
    margin: 0 0 30px;
    padding: 0;
    min-height: 0;
}
#peManagement .vm-pe .questionBox.popup .questionBody {
    margin: 0 0 8px;
}
#peManagement .vm-pe .vm-widget-body {
    padding: 0;
}
#peManagement .vm-pe .questionBox .hoverable li td.last,
#peManagement .vm-pe .questionBox .hoverable li:hover td.last {
    border-bottom: none;
}
#peManagement .vm-pe .questionBox.number label,
#peManagement .vm-pe .questionBox .questionOptions input.maintext,
#peManagement .vm-pe .questionBox .questionOptions input.subtext {
    border-radius: 0;
}

/* PDF layout: letter page, readable type */
.bm-pdf-summary {
    padding: 0;
    font-size: 13px;
}
.bm-pdf-summary h3 {
    font-size: 20px !important;
    margin: 10px 0 !important;
}
</style>
